/**
 * Fees & Costs Tab Component
 */

import { useSearchParams } from 'react-router';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import type { Fund } from '@/types';
import { useFundInvestorStatistics } from '@/hooks/react-query/useInvestors';
import { FundFeesFundsCosts as FundCostsSection } from './FundFeesFundsCosts';
import { FundFeesDealCosts as DealCostsSection } from './FundFeesDealCosts';
import { FundFeesManagementFees as ManagementFeesSection } from './FundFeesManagementFees';
import { FundFeesAccruals } from './FundFeesAccruals';

interface FundFeesAndCostsTabProps {
  fund: Fund;
}

export function FundFeesAndCostsTab({ fund }: FundFeesAndCostsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('subsection') || 'fee-accruals';

  const setActiveSection = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('subsection', value);
      return newParams;
    });
  };
  const { data: fundStats } = useFundInvestorStatistics(fund.id);
  const committedCapital = fundStats?.total_commitments ?? 0;

  return (
    <Tabs value={activeSection} onValueChange={setActiveSection}>
      <TabsContent value="fee-accruals">
        <FundFeesAccruals fundId={fund.id} fundCurrency={fund.base_currency} />
      </TabsContent>

      <TabsContent value="management-fees">
        <ManagementFeesSection fundId={fund.id} fund={fund} />
      </TabsContent>

      <TabsContent value="fund-costs">
        <FundCostsSection
          fundId={fund.id}
          fundCurrency={fund.base_currency}
          committedCapital={committedCapital}
          capPercentage={
            fund.fund_costs_cap != null ? fund.fund_costs_cap * 100 : undefined
          }
        />
      </TabsContent>

      <TabsContent value="deal-costs">
        <DealCostsSection fundId={fund.id} fundCurrency={fund.base_currency} />
      </TabsContent>
    </Tabs>
  );
}
